import sanitizeHtml from 'sanitize-html';
import { PatientSurvey } from '../types/PatientSurvey.type';
import {
  CheckboxAnswer,
  SurveyQuestion,
  SurveyQuestionIf,
  SurveyQuestionIfType,
} from '../types/Survey.type';
import { buildGroupSubQuestionsStr } from './buildGroupSubQuestionsStr';
import { formatSurveyDateValue } from './formatSurveyDateValue';
import { isEmpty } from './isEmpty';
import { escapeHtml } from './sanitizeChatHtml';

export const buildCompletedMedicationSurvey = (survey: PatientSurvey, plain?: boolean) => {
  const surveyStr = survey?.survey?.questions?.reduce((str, surveyQuestion: SurveyQuestion) => {
    if (
      surveyQuestion?.id === '5145e776-c1ff-407a-97de-61647a3acf4c' &&
      surveyQuestion?.answer?.value === '1bae9487-6956-4db0-99aa-807a6c21df01'
    ) {
      return (str += plain
        ? 'Current medications:\nPatient denies current use of any medications.\n'
        : '<p style="font-weight: bold;">Current medications:</p><p style="margin-left: 16px; color: #9CA3AF;">Patient denies current use of any medications.</p>');
    } else if (
      surveyQuestion?.id === 'ff7038bc-3f08-40cd-a819-491e590b6163' &&
      !!surveyQuestion?.answer?.value?.length
    ) {
      return (str += plain
        ? `Current medications:\nPatient reports:\n${surveyQuestion?.answer?.value}\n`
        : `<p style="font-weight: bold;">Current medications:</p><p style="margin-left: 16px; font-weight: bold;">Patient reports:</p><ul style="margin-left: 16px; color: #93cc68;">${escapeHtml(String(surveyQuestion?.answer?.value))}</ul>`);
    } else if (
      surveyQuestion?.id === 'ed0665d9-02b4-4ce3-80b1-2f7d260d1dc7' &&
      surveyQuestion?.answer?.value === '76a45752-3bc0-4542-b029-8e41bc002e20'
    ) {
      return (str += plain
        ? 'Current supplements:\nPatient denies current use of any supplements.\n'
        : '<p style="font-weight: bold;">Current supplements:</p><p style="margin-left: 16px; color: #9CA3AF;">Patient denies current use of any supplements.</p>');
    } else if (
      surveyQuestion.id === 'f9ee72d1-ad4a-486e-96ba-869b42ca7973' &&
      !surveyQuestion?.answer?.value?.length
    ) {
      return (str += plain
        ? 'Recent medications:\nPatient denies recently taking any of the following meds: Blood thinners (Coumadin, Warfarin), Flouroquinilone antibiotics (Levoquin), Steroids, and Birth control pills.\n'
        : '<p style="font-weight: bold;">Recent medications:</p><p style="margin-left: 16px; color: #9CA3AF;">Patient denies recently taking any of the following meds: Blood thinners (Coumadin, Warfarin), Flouroquinilone antibiotics (Levoquin), Steroids, and Birth control pills.</p>');
    } else if (
      surveyQuestion.id === '12a4de70-ec8f-4463-9b02-cb3128a9dbdc' &&
      !!surveyQuestion?.answer?.value?.length
    ) {
      return (str += plain
        ? `Current supplements:\nPatient reports:\n${surveyQuestion?.answer?.value}\n`
        : `<p style="font-weight: bold;">Current supplements:</p><p style="margin-left: 16px; font-weight: bold;">Patient reports:</p><span style="margin-left: 16px; color: #93cc68;">${escapeHtml(String(surveyQuestion?.answer?.value))}</span>`);
    } else if (surveyQuestion.type === 'checkbox') {
      const answeredSelections = surveyQuestion?.options
        ?.filter(option => surveyQuestion?.answer?.value.includes(option.id))
        .map(option =>
          plain
            ? `- ${option.display}`
            : `<li style="margin-left: 20px; color: #93cc68;">${escapeHtml(String(option.display))}</li>`
        )
        .join(plain ? '' : ' ');

      const deniedSelections =
        surveyQuestion?.options
          ?.filter(option => !surveyQuestion?.answer?.value.includes(option.id))
          .map(option => option.display)
          .join(', ') || '';

      str += plain
        ? `Recent medications:\nPatient reports:\n${answeredSelections}\n${
            deniedSelections?.length ? `Patient denies: ${deniedSelections}\n` : ''
          }`
        : `<p style="font-weight: bold;">Recent medications:</p><p style="margin-left: 16px; font-weight: bold;">Patient reports:</p><ul style="margin-left: 16px; color: #93cc68;">${answeredSelections}</ul>${
            deniedSelections?.length
              ? `<p style="margin-left: 36px;"><span style="font-weight: bold;">Patient denies:</span><span style="margin-left: 2px; color: #9CA3AF;"> ${escapeHtml(deniedSelections)}</span></p>`
              : ''
          }`;
    }

    return str;
  }, '');

  return '<h3 style="font-weight: bold;">Medication history</h3>' + surveyStr;
};

export const buildCompletedAllergySurvey = (survey: PatientSurvey) => {
  const filteredQuestions = survey?.survey?.questions?.filter(
    surveyQuestion => surveyQuestion.type === 'radio'
  );

  const allNos = filteredQuestions?.every(
    question =>
      question?.answer?.value === '9c4f9aa7-640a-4f0a-8dfe-3e463a8eee27' ||
      question?.answer?.value === '3da8b48a-1684-4d51-acff-11563b5e2c6b'
  );

  if (allNos) {
    return '<h3 style="font-weight: bold;">Allergy survey</h3><p style="font-weight: bold;">Known allergies:</p><p style="margin-left: 16px; color: #9CA3AF;;">Patient denies any known allergies.</p>';
  }

  const surveyStr = survey?.survey?.questions?.reduce((str, surveyQuestion: SurveyQuestion) => {
    if (
      surveyQuestion?.id === 'e5dce05c-6c74-44bf-b44f-b1730b68f56b' &&
      !!surveyQuestion?.answer?.value?.length
    ) {
      return (str += `<p style="font-weight: bold;">Medication, food or insect bites allergies:</p><span style="margin-left: 16px; font-weight: bold;">Patient reports:</span><p style="margin-left: 16px; color: #93cc68;"> ${escapeHtml(String(surveyQuestion?.answer?.value))}</p>`);
    } else if (
      surveyQuestion?.id === 'e5dce05c-6c74-44bf-b44f-b1730b68f56b' &&
      !surveyQuestion?.answer?.value?.length
    ) {
      return (str += `<p style="font-weight: bold;">Medication, food or insect bites allergies:</p><p style="margin-left: 16px; color: #9CA3AF";;>Patient denies any known medication, food or insect bites allergies.</p>`);
    } else if (
      surveyQuestion.id === 'f552f022-c7c7-4944-9b22-5350e9096fe7' &&
      !!surveyQuestion?.answer?.value?.length
    ) {
      return (str += `<p style="font-weight: bold;">Environmental or seasonal allergies:</p><p style="margin-left: 16px; font-weight: bold;">Patient reports:</p><span style="margin-left: 16px; color: #93cc68;;"> ${escapeHtml(String(surveyQuestion?.answer?.value))}</span>`);
    } else if (
      surveyQuestion.id === 'f552f022-c7c7-4944-9b22-5350e9096fe7' &&
      !surveyQuestion?.answer?.value?.length
    ) {
      return (str += `<p style="font-weight: bold;">Environmental or seasonal allergies:</p><p style="margin-left: 16px; color: #9CA3AF"">Patient denies any known environmental or seasonal allergies.</p>`);
    }

    return str;
  }, '');

  return '<h3 style="font-weight: bold;">Allergy survey</h3>' + surveyStr;
};

/**
 * Check if a single `if` / `ifSome` predicate is satisfied.
 * `questionsById` is a lookup of all questions in the survey keyed by id.
 */
const evaluateCondition = (
  cond: SurveyQuestionIf,
  questionsById: Record<string, SurveyQuestion>
): boolean => {
  if (!cond?.id) return true;
  const ref = questionsById[cond.id];
  const answer = ref?.answer;
  const rawValue = answer?.value;

  if (cond.type === SurveyQuestionIfType.notAnswered) {
    return (
      rawValue == null || rawValue === '' || (Array.isArray(rawValue) && rawValue.length === 0)
    );
  }

  // Normalise to an array of answered values for comparison
  const answered: (string | number)[] = Array.isArray(rawValue)
    ? rawValue
    : rawValue != null && rawValue !== ''
      ? [rawValue as string | number]
      : [];

  if (cond.type === SurveyQuestionIfType.equal) {
    const expected = Array.isArray(cond.value) ? cond.value : [cond.value];

    return expected.some(exp => answered.some(ans => String(ans) === String(exp)));
  }

  if (cond.type === SurveyQuestionIfType.lessThan) {
    return answered.some(v => Number(v) < Number(cond.value));
  }

  if (cond.type === SurveyQuestionIfType.greaterThan) {
    return answered.some(v => Number(v) > Number(cond.value));
  }

  return true;
};

/**
 * Return true when all `if` conditions AND at least one `ifSome` condition are satisfied
 * (matching the runtime survey rendering logic).
 */
const questionConditionsMet = (
  question: SurveyQuestion,
  questionsById: Record<string, SurveyQuestion>
): boolean => {
  const ifOk =
    Array.isArray(question.if) && question.if.length
      ? question.if.every(c => evaluateCondition(c, questionsById))
      : true;
  const ifSomeOk =
    Array.isArray(question.ifSome) && question.ifSome.length
      ? question.ifSome.some(c => evaluateCondition(c, questionsById))
      : true;

  return ifOk && ifSomeOk;
};

export const buildCompletedSurvey = (survey: PatientSurvey) => {
  const questions = survey?.survey?.english?.questions || survey?.survey?.questions;

  // Build a lookup so condition evaluation can reference any question by id,
  // including sub-questions inside groups (with their answers resolved).
  const questionsById: Record<string, SurveyQuestion> = {};

  for (const q of questions || []) {
    if (q?.id) questionsById[q.id] = q;

    if (q?.type === 'group' && q.answer?.type === 'group') {
      for (const sub of q.typeOptions?.subQuestions || []) {
        const subAnswer = q.answer.value?.[sub.id];
        questionsById[sub.id] = subAnswer ? { ...sub, answer: subAnswer } : sub;
      }
    }
  }

  const surveyStr = questions?.reduce((str, surveyQuestion: SurveyQuestion) => {
    if (!surveyQuestion?.answer) return str;

    // Skip questions whose conditional visibility was not met
    if (!questionConditionsMet(surveyQuestion, questionsById)) return str;

    if (surveyQuestion?.type === 'slider') {
      return (str += getSliderQuestionStr(surveyQuestion));
    } else if (surveyQuestion?.type === 'radio') {
      return (str += getRadioQuestionStr(surveyQuestion));
    } else if (surveyQuestion?.type === 'group') {
      return (str += getGroupQuestionStr(surveyQuestion));
    } else if (surveyQuestion?.type === 'checkbox') {
      return (str += getCheckBoxStr(surveyQuestion));
    } else if (surveyQuestion?.type === 'textarea' || surveyQuestion?.type === 'input') {
      return (str += getInputStr(surveyQuestion));
    } else if (surveyQuestion?.type === 'date') {
      return (str += getDateQuestionStr(surveyQuestion));
    } else if (surveyQuestion?.type === 'combobox') {
      return (str += getComboboxQuestionStr(surveyQuestion));
    }

    return str;
  }, '');

  return `<h3 style="font-weight: bold;">${escapeHtml(String(survey?.survey?.english?.name || survey?.survey?.name))}${
    survey?.score
      ? ` - Score: ${escapeHtml(String(survey.score))}</h3>
    `
      : '</h3>'
  }${surveyStr}`;
};

// Preserve readable authored entities after stripping label markup, while
// preventing incomplete tags from consuming the generated answer HTML.
const getQuestionStr = (question: string) =>
  sanitizeHtml(question?.replace(/<[^>]*>/g, '') || '', {
    allowedTags: [],
    allowedAttributes: {},
  });

const getSliderQuestionStr = (surveyQuestion: SurveyQuestion) => {
  const question = getQuestionStr(surveyQuestion?.question);

  return `
        <p>${question}
        <span style="font-weight: bold; color: #93cc68;">
        ${
          surveyQuestion.answer?.type === 'slider' && !isEmpty(surveyQuestion.answer?.value)
            ? `${escapeHtml(String(surveyQuestion.answer.value))} / ${escapeHtml(String(surveyQuestion.typeOptions?.max))}`
            : 'No value selected'
        }
        </span></p>
    `;
};

const getRadioQuestionStr = (surveyQuestion: SurveyQuestion, answerValue?: any) => {
  const question = getQuestionStr(surveyQuestion?.question);
  const selectedDisplay = surveyQuestion?.options?.find(
    option => option?.id === surveyQuestion?.answer?.value || option?.id === answerValue
  )?.display;
  const answerDisplay = selectedDisplay
    ? escapeHtml(String(selectedDisplay))
    : surveyQuestion?.answer?.otherValue
      ? `Other - (${escapeHtml(surveyQuestion.answer.otherValue)})`
      : '';

  return `<p>${question}
        <span style="font-weight: bold;  color: #93cc68;">${answerDisplay}</span></p>`;
};

// Note: unlike buildSurveyStr's group renderer, this one intentionally emits
// only the sub-answers — generated notes have never included the group's own
// label (pre-existing behavior carried over from main).
const getGroupQuestionStr = (surveyQuestion: SurveyQuestion) =>
  buildGroupSubQuestionsStr(surveyQuestion, {
    radio: getRadioQuestionStr,
    checkbox: getCheckBoxStr,
    slider: getSliderQuestionStr,
    input: getInputStr,
    date: getDateQuestionStr,
  });

const getCheckBoxStr = (surveyQuestion: SurveyQuestion) => {
  const question = getQuestionStr(surveyQuestion?.question);
  let answeredSelections = surveyQuestion?.options
    ?.filter(option => surveyQuestion?.answer?.value?.includes(option.id))
    .map(
      option =>
        '<li style="margin-left: 20px; color: #93cc68;">' +
        escapeHtml(String(option.display)) +
        '</li>'
    )
    .join(' ');
  const otherQuestionAnswer = surveyQuestion?.answer as CheckboxAnswer;

  if (otherQuestionAnswer?.otherValue) {
    // Patient-typed value rendered via dangerouslySetInnerHTML — escape it.
    const escapedOther = escapeHtml(otherQuestionAnswer.otherValue);

    answeredSelections += answeredSelections?.length
      ? `<li style="margin-left: 20px; color: #93cc68;"> Other - (${escapedOther})</li>`
      : `<li style="margin-left: 20px; color: #93cc68;">Other - (${escapedOther})</li>`;
  }

  const deniedSelections =
    surveyQuestion?.options
      ?.filter(option => !surveyQuestion?.answer?.value?.includes(option.id))
      .map(option => escapeHtml(String(option.display)))
      .join(', ') || '';

  return `<p>${question}</p>
      ${
        surveyQuestion?.answer?.value?.length > 0
          ? `<p style="margin-left: 16px; font-weight: bold;">Patient reports:</p> <ul style="margin-left: 16px; font-weight: semi-bold; color: #93cc68;">${answeredSelections}</ul>`
          : ''
      }
      ${
        deniedSelections?.length
          ? `<p style="margin-left: 16px;"><span style="font-weight: bold;">Patient denies:</span> <span style="margin-left: 2px; color: #9CA3AF;">${deniedSelections}</span></p>`
          : ''
      }</p>`;
};

const getDateQuestionStr = (surveyQuestion: SurveyQuestion) => {
  if (!surveyQuestion?.answer?.value) return '';
  const question = getQuestionStr(surveyQuestion?.question);
  const display = formatSurveyDateValue(surveyQuestion.answer.value);

  if (!display) return '';

  return `<p>${question} <span style="font-weight: bold; color: #93cc68;">${escapeHtml(String(display))}</span></p>`;
};

const getComboboxQuestionStr = (surveyQuestion: SurveyQuestion) => {
  if (!surveyQuestion?.answer?.value) return '';
  const question = getQuestionStr(surveyQuestion?.question);
  const value = surveyQuestion.answer.value;
  const values = Array.isArray(value) ? value : [value];
  const displays = values
    .map(v => {
      const opt = surveyQuestion?.options?.find(o => o.id === v);

      return escapeHtml(String(opt?.display || v));
    })
    .filter(Boolean);

  if (!displays.length) return '';

  return `<p>${question} <span style="font-weight: bold; color: #93cc68;">${displays.join(', ')}</span></p>`;
};

const getInputStr = (surveyQuestion: SurveyQuestion) => {
  if (!surveyQuestion?.answer?.value) return '';
  const question = getQuestionStr(surveyQuestion?.question);

  return `<p>${question} <span style="font-weight: bold; color: #93cc68;">
        ${
          surveyQuestion?.answer?.value
            ? escapeHtml(String(surveyQuestion.answer.value))
            : 'No value entered'
        }
    </span></p>`;
};
